/**
 * The one date picker every task date uses (US 4.2): New task, task detail,
 * quick actions, the Calendar view and filters.
 *
 * Shortcuts (Today, Tomorrow, Next week, No date), a field that takes typed
 * dates ("24/10", "next Thu"), a month calendar starting on the Workspace's
 * first day of the week — Arabic month names and right-to-left in Arabic —
 * and an optional time.
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { CalendarDays, ChevronLeft, ChevronRight, Clock, X } from "lucide-react";
import { cn } from "@weco/ui";
import { Dropdown } from "../ui";
import { useTasksSettings } from "./api";
import { DUE_TONE_CLASS, addDays, formatTaskDate, iso, monthCells, nextWeekStart, parseIso, parseTypedDate } from "./dates";

export interface DateValue {
  date: string | null;
  time: string | null;
}

export function DatePickerPanel({
  value,
  allowTime = true,
  min,
  onChange,
  onDone,
}: {
  value: DateValue;
  allowTime?: boolean;
  /** Earliest allowed date (a due date can't be before the start date). */
  min?: string | null;
  onChange: (value: DateValue) => void;
  onDone?: () => void;
}) {
  const { t, i18n } = useTranslation();
  const settings = useTasksSettings().data;
  const weekStart = settings?.week_start ?? 0;
  const locale = i18n.language?.startsWith("ar") ? "ar" : i18n.language || "en";
  const today = new Date();
  const shown = value.date ? parseIso(value.date) : today;
  const [cursor, setCursor] = useState({ y: shown.getFullYear(), m: shown.getMonth() });
  const [typed, setTyped] = useState("");
  const [typedError, setTypedError] = useState("");
  const [time, setTime] = useState(value.time ?? "");
  useEffect(() => setTime(value.time ?? ""), [value.time]);

  const pick = (date: string | null, keepOpen = false) => {
    onChange({ date, time: date ? time || null : null });
    if (!keepOpen) onDone?.();
  };
  const tooEarly = (d: string) => !!min && d < min;
  const cells = monthCells(cursor.y, cursor.m, weekStart);
  const weekdays = cells.slice(0, 7).map((d) => d.toLocaleDateString(locale, { weekday: "narrow" }));
  const monthLabel = new Date(cursor.y, cursor.m, 1).toLocaleDateString(locale, { month: "long", year: "numeric" });
  const shift = (n: number) => {
    const d = new Date(cursor.y, cursor.m + n, 1);
    setCursor({ y: d.getFullYear(), m: d.getMonth() });
  };
  const shortcuts: [string, string | null][] = [
    [t("tasks.dates.today", { defaultValue: "Today" }), iso(today)],
    [t("tasks.dates.tomorrow", { defaultValue: "Tomorrow" }), iso(addDays(today, 1))],
    [t("tasks.dates.nextWeek", { defaultValue: "Next week" }), nextWeekStart(weekStart, today)],
    [t("tasks.dates.noDate", { defaultValue: "No date" }), null],
  ];

  return (
    <div className="w-72 p-2" onClick={(e) => e.stopPropagation()}>
      <input
        autoFocus
        value={typed}
        onChange={(e) => {
          setTyped(e.target.value);
          setTypedError("");
        }}
        onKeyDown={(e) => {
          if (e.key !== "Enter") return;
          e.preventDefault();
          const parsed = parseTypedDate(typed, today);
          if (!parsed) {
            setTypedError(t("tasks.dates.notADate", { defaultValue: "That isn't a date we can use." }));
            return;
          }
          if (tooEarly(parsed)) {
            setTypedError(t("tasks.dates.dueBeforeStart", { defaultValue: "Due date can't be before the start date." }));
            return;
          }
          pick(parsed);
        }}
        aria-label={t("tasks.dates.typeDate", { defaultValue: "Type a date — 24/10, next Thu" })}
        placeholder={t("tasks.dates.typeDate", { defaultValue: "Type a date — 24/10, next Thu" })}
        aria-invalid={!!typedError}
        className={cn(
          "w-full h-9 px-3 mb-2 bg-transparent border rounded-lg text-sm outline-none focus:ring-1",
          typedError ? "border-red-500 focus:ring-red-500/40" : "border-border focus:border-foreground/60 focus:ring-foreground/30",
        )}
      />
      {typedError && (
        <p role="alert" className="text-xs text-red-500 -mt-1 mb-2 px-1">
          {typedError}
        </p>
      )}
      {min && (
        <p className="text-[11px] text-muted-foreground mb-2 px-1">
          {t("tasks.dates.notBefore", { defaultValue: "Not before {{date}}", date: parseIso(min).toLocaleDateString(locale, { day: "numeric", month: "short" }) })}
        </p>
      )}
      <div className="grid grid-cols-2 gap-1 mb-2">
        {shortcuts.map(([label, date]) => (
          <button
            key={label}
            type="button"
            disabled={!!date && tooEarly(date)}
            onClick={() => pick(date)}
            className={cn(
              "h-8 px-2 rounded-lg text-xs font-semibold text-start hover:bg-muted transition-colors disabled:opacity-40",
              value.date === date && "bg-primary/10 text-primary",
            )}
          >
            {label}
          </button>
        ))}
      </div>
      <div className="flex items-center justify-between px-1 mb-1">
        <button type="button" onClick={() => shift(-1)} aria-label={t("tasks.hubs.calendar.prev", { defaultValue: "Previous month" })} className="w-7 h-7 rounded-md hover:bg-muted flex items-center justify-center">
          <ChevronLeft className="size-4 rtl:rotate-180" />
        </button>
        <span className="text-sm font-bold">{monthLabel}</span>
        <button type="button" onClick={() => shift(1)} aria-label={t("tasks.hubs.calendar.next", { defaultValue: "Next month" })} className="w-7 h-7 rounded-md hover:bg-muted flex items-center justify-center">
          <ChevronRight className="size-4 rtl:rotate-180" />
        </button>
      </div>
      <div className="grid grid-cols-7 text-center" role="grid" aria-label={monthLabel}>
        {weekdays.map((w, i) => (
          <span key={i} className="text-[10px] font-semibold text-muted-foreground py-1">
            {w}
          </span>
        ))}
        {cells.map((d) => {
          const key = iso(d);
          const inMonth = d.getMonth() === cursor.m;
          const selected = value.date === key;
          const isToday = key === iso(today);
          return (
            <button
              key={key}
              type="button"
              role="gridcell"
              aria-selected={selected}
              aria-label={d.toLocaleDateString(locale, { dateStyle: "full" })}
              disabled={tooEarly(key)}
              onClick={() => pick(key, allowTime)}
              className={cn(
                "h-8 m-px rounded-lg text-xs tabular-nums transition-colors disabled:opacity-30 disabled:cursor-not-allowed",
                selected ? "bg-primary text-primary-foreground font-bold" : "hover:bg-muted",
                !selected && !inMonth && "text-muted-foreground/50",
                !selected && isToday && "ring-1 ring-primary text-primary font-bold",
              )}
            >
              {d.toLocaleDateString(locale, { day: "numeric" })}
            </button>
          );
        })}
      </div>
      {allowTime && (
        <div className="flex items-center gap-2 mt-2 pt-2 border-t border-border">
          <Clock className="size-4 text-muted-foreground shrink-0" />
          <input
            type="time"
            value={time}
            disabled={!value.date}
            onChange={(e) => {
              setTime(e.target.value);
              if (value.date) onChange({ date: value.date, time: e.target.value || null });
            }}
            aria-label={t("tasks.dates.time", { defaultValue: "Time" })}
            className="flex-1 h-8 px-2 bg-transparent border border-border rounded-lg text-sm outline-none focus:border-foreground/60 disabled:opacity-40"
          />
          {time && (
            <button
              type="button"
              onClick={() => {
                setTime("");
                if (value.date) onChange({ date: value.date, time: null });
              }}
              aria-label={t("tasks.dates.clearTime", { defaultValue: "Remove time" })}
              className="w-7 h-7 rounded-md hover:bg-muted flex items-center justify-center text-muted-foreground"
            >
              <X className="size-3.5" />
            </button>
          )}
          <button type="button" onClick={onDone} className="h-8 px-3 rounded-lg bg-foreground text-background text-xs font-bold">
            {t("tasks.dates.done", { defaultValue: "Done" })}
          </button>
        </div>
      )}
    </div>
  );
}

/** A date chip that opens the picker — what task fields use. */
export function DateField({
  value,
  timeZone,
  allowTime = true,
  min,
  done,
  disabled,
  placeholder,
  label,
  onChange,
  className,
}: {
  value: DateValue;
  timeZone: string;
  allowTime?: boolean;
  min?: string | null;
  done?: boolean;
  disabled?: boolean;
  placeholder?: string;
  label: string;
  onChange: (value: DateValue) => void;
  className?: string;
}) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const shown = formatTaskDate(value.date, value.time, timeZone, t, i18n.language, { done });
  return (
    <span className="relative inline-flex">
      <button
        type="button"
        disabled={disabled}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((o) => !o);
        }}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={label}
        title={shown?.title}
        className={cn(
          "flex items-center gap-1.5 text-sm px-2 py-0.5 rounded hover:bg-muted transition-colors font-medium disabled:hover:bg-transparent disabled:cursor-default",
          shown ? DUE_TONE_CLASS[shown.tone] : "text-muted-foreground",
          className,
        )}
      >
        <CalendarDays className="size-[13px]" />
        {shown ? shown.label : placeholder ?? t("tasks.dates.noDate", { defaultValue: "No date" })}
      </button>
      <Dropdown open={open} onClose={() => setOpen(false)} className="p-0">
        <DatePickerPanel value={value} allowTime={allowTime} min={min} onChange={onChange} onDone={() => setOpen(false)} />
      </Dropdown>
    </span>
  );
}
